import { DownloadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Table,
  TablePaginationConfig,
} from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { data, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ParcelStatusContext } from '../../context/ParcelStatusProvider';
import CustomerSelect from '../../lib/CustomerSelect';
import DeliveryManSelect from '../../lib/DeliveryManSelect';
import { notifyError, priceFormat } from '../../store/utils';
import { API } from '../../api';
import BranchSelect from '../../lib/BranchSelect';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { PagedResultState } from '../../common/CommonResult';
import { render } from '@testing-library/react';

const branchLabel: any = {
  NEW: 'Ав.Салбар',
  RECEIVED: 'ИА Салбар',
  SENT_TO_TML: 'ИА Салбар',
  RECEIVED_TO_TML: 'ИА Салбар',
};

const dateLabel: any = {
  NEW: 'Ав.Огноо',
  RECEIVED: 'Скан огноо',
  SENT_TO_TML: 'Скан огноо',
  RECEIVED_TO_TML: 'Скан огноо',
};

const deliveryManLabel: any = {
  NEW: 'ИА Хүргэгч',
  RECEIVED: 'ИА Хүргэгч',
  SENT_TO_TML: 'ИА Хүргэгч',
  RECEIVED_TO_TML: 'ИА Хүргэгч',
};

const ShippingStatusReport = () => {
  const { status } = useParams();
  const { statusList } = useContext(ParcelStatusContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dateList, setDateList] = useState([]);
  const [dateLoading, setDateLoading] = useState(true);

  const [selectRow, setSelectRow] = useState<any>(undefined);

  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const download = useCallback(async () => {
    try {
      setDownloadLoading(true);
      let params = queryString.parse(location.search);
      const query = {
        branchNo: params.branchNo || '',
        startDate: params?.startDate
          ? dayjs(selectRow?.record?.date).startOf('day').toISOString()
          : '',
        endDate: params?.endDate ? dayjs(selectRow?.record?.date).endOf('day').toISOString() : '',
        customerNo: params.customerNo || '',
        deliveryManNo: params.deliveryManNo || '',
        svc: params.svc || '',
        reason: params.reason || '',
        status,
        page: 1,
        size: 20,
      };
      const response = await API.get({ apiVersion: 'api', responseType: 'blob' })(
        `/report/status/date/line-item/download?${queryString.stringify(query)}`
      );
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'data.xlsx');
      document.body.appendChild(link);
      link.click();
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setDownloadLoading(false);
    }
  }, [selectRow, location, status]);

  const title = useMemo(() => {
    const item = statusList.find((it: any) => it.cd.toUpperCase() === status?.toUpperCase());
    return item?.name ? `${item?.name} тайлан` : '';
  }, [statusList, status]);

  const fetchDates = useCallback(async () => {
    try {
      setDateLoading(true);
      const payload: any = queryString.parse(location.search);
      payload.status = status;

      const { result } = await API.get({ apiVersion: 'api' })(
        `/report/status/date?${queryString.stringify(payload)}`
      );
      setDateList(
        (result || []).map((item: any, index: number) => {
          return {
            key: index,
            ...item,
          };
        })
      );
      setSelectRow(undefined);
      setState({ loading: false, result: {} });
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setDateLoading(false);
    }
  }, [location, status]);

  const fetchLineItems = useCallback(async () => {
    try {
      setState({
        loading: true,
        result: state.result || {},
      });
      let params = queryString.parse(location.search);
      const query = {
        branchNo: params.branchNo || '',
        startDate: params?.startDate
          ? dayjs(selectRow?.record?.date).startOf('day').toISOString()
          : '',
        endDate: params?.endDate ? dayjs(selectRow?.record?.date).endOf('day').toISOString() : '',
        customerNo: params.customerNo || '',
        deliveryManNo: params.deliveryManNo || '',
        svc: params.svc || '',
        reason: params.reason || '',
        status,
        page: selectRow?.page || 1,
        size: selectRow?.size || 20,
      };
      const { result } = await API.get({ apiVersion: 'api' })(
        `/report/status/date/line-item?${queryString.stringify(query)}`
      );
      setState({
        loading: false,
        result: result,
      });
    } catch (error: any) {
      notifyError(error.message);
      setState({
        loading: false,
        result: state.result || {},
      });
    }
  }, [selectRow, location, status]);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      setSelectRow({
        ...selectRow,
        page: pagination.current || 1,
        size: pagination.pageSize || 20,
      });
    },
    [location, navigate, location, selectRow]
  );

  useEffect(() => {
    if (!location.search) {
      let params = queryString.parse(location.search);
      const query = {
        branchNo: params.branchNo || '',
        startDate: params?.startDate
          ? dayjs(params?.startDate as string)
              .startOf('day')
              .toISOString()
          : '',
        endDate: params?.endDate
          ? dayjs(params?.endDate as string)
              .endOf('day')
              .toISOString()
          : '',
        customerNo: params.customerNo || '',
        deliveryManNo: params.deliveryManNo || '',
        svc: params.svc || '',
        reason: params.reason || '',
        page: 1,
        size: 20,
      };
      navigate(`/report/${status}?${queryString.stringify(query)}`);
    }
  }, [status]);

  useEffect(() => {
    if (location.search) {
      fetchDates();
    }
  }, [location.search, status]);

  useEffect(() => {
    if (selectRow !== undefined) {
      fetchLineItems();
    }
  }, [selectRow, location.search, status]);

  const columnsDate = useMemo(() => {
    return [
      {
        title: 'Нэр',
        key: 'name',
        width: 100,
        render: (_val: string, row: any) => <span>{dayjs(row.date).format('YYYY-MM-DD')}</span>,
      },
      {
        title: 'Тоо Ш ',
        render: (_val: string, row: any) => <div>{row.total}</div>,
      },
      {
        title: 'Үнэ',
        render: (_val: string, row: any) => <span>{priceFormat(row.amount)}</span>,
      },
    ];
  }, []);

  const columns = useMemo(() => {
    const columns: any = [
      {
        title: '№',
        key: 'dataIndex',
        width: 40,
        render: (val: string, row: any, index: number) => <span>{index + 1}</span>,
      },
    ];
    if (status === 'new') {
      columns.push(
        {
          title: 'Захиалга огноо',
          key: 'orderNumber',
          width: 100,
          render: (_val: any, row: any) => (
            <span>{dayjs(row.createdAt).format('YYYY-MM-DD HH:mm')}</span>
          ),
        },
        {
          title: 'Баримт ',
          width: 120,
          dataIndex: 'barcode',
        },
        {
          title: 'Салбар',
          width: 100,
          render: (_val: any, row: any) => <span>{row.firstBranchName}</span>,
        }
      );
    } else if (status === 'received' || status === 'sent_to_tml' || status === 'received_to_tml') {
      columns.push(
        {
          title: 'Скан огноо',
          key: 'orderNumber',
          width: 100,
          render: (_val: any, row: any) => <span>{row.createdAt}</span>,
        },
        {
          title: 'Баримт ',
          width: 120,
          render: (_val: any, row: any) => <div>{row.barcode}</div>,
        },
        {
          title: 'ИА Салбар',
          width: 100,
          render: (_val: any, row: any) => <span>{row.firstBranchName}</span>,
        },
        {
          title: 'ИА Хүргэгч',
          width: 100,
          render: (_val: any, row: any) => <span>{row.firstDeliveryManName}</span>,
        }
      );
    } else {
      columns.push(
        {
          title: 'Скан огноо',
          key: 'orderNumber',
          width: 100,
          render: (_val: any, row: any) => (
            <span>{dayjs(row.createdAt).format('YYYY-MM-DD HH:mm')}</span>
          ),
        },
        {
          title: 'Баримт ',
          width: 120,
          render: (_val: any, row: any) => <div>{row.barcode}</div>,
        },
        {
          title: 'Хү Салбар',
          width: 100,
          render: (_val: any, row: any) => <span>{row.lastBranchName}</span>,
        },
        {
          title: 'Хү Хүргэгч',
          width: 100,
          render: (_val: any, row: any) => <span>{row.lastDeliveryManName}</span>,
        }
      );
    }

    columns.push(
      {
        title: 'Илгээгч',
        width: 120,
        render: (_val: any, row: any) => <span>{row.senderName}</span>,
      },
      {
        title: 'Харилцагч',
        width: 120,
        render: (_val: any, row: any) => <span>{row.customerName}</span>,
      }
    );
    if (status === 'new') {
      columns.push({
        title: 'Хэмжээ',
        dataIndex: 'weightTypeName',
        width: 80,
        render: (val: string) => <span>{val}</span>,
      });
    }
    columns.push(
      {
        title: 'Тоо',
        width: 80,
        render: (_val: any) => <span>1</span>,
      },
      {
        title: 'Үнэ',
        dataIndex: 'price',
        width: 80,
        render: (_val: any) => <span>{priceFormat(_val)}₮</span>,
      }
    );

    if (status === 'delivery_completed') {
      columns.push(
        {
          title: 'Хугацаа',
          render: (_val: any, row: any) => <span>{row.time || ''}</span>,
        },
        {
          title: 'Хүлээн авагч',
          width: 80,
          render: (_val: any, row: any) => <span>{row.receiver || ''}</span>,
        }
      );
    } else if (status === 'not_delivered') {
      columns.push({
        title: 'Шалтгаан',
        width: 80,
        render: (_val: any, row: any) => {
          let reason = row.reason;
          if (reason) {
            let ss = reason.split(':');
            if (ss.length > 1) {
              return <span>{ss[1]}</span>;
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
      });
    }
    columns.push({
      title: 'Төлөв',
      width: 80,
      render: (_val: any, row: any) => <span>{row.status || ''}</span>,
    });
    return columns;
  }, [status]);

  return (
    <Card
      title={title}
      extra={
        <Button loading={downloadLoading} onClick={() => download()} icon={<DownloadOutlined />}>
          Татах
        </Button>
      }
    >
      <ReportSearch />
      <Row gutter={15}>
        <Col span={6}>
          <Table
            rowKey={(_record) => `date_item_${_record.key}`}
            size="small"
            className="date-list-table"
            columns={columnsDate}
            loading={dateLoading}
            pagination={false}
            bordered
            onRow={(_record) => ({
              style: { cursor: 'pointer' },
              onClick: () => {
                setSelectRow({
                  record: _record,
                  page: 1,
                  size: 20,
                });
              },
            })}
            rowClassName={(record) =>
              record.key === selectRow?.record?.key ? 'table-select-row' : ''
            }
            dataSource={dateList}
          />
        </Col>
        <Col span={18}>
          <Table
            rowKey={(_record, index) => `row_item_${index}`}
            size="small"
            bordered
            loading={state.loading}
            columns={columns}
            dataSource={state?.result?.data || []}
            onChange={handleTableChange}
            pagination={{
              current: state?.result?.page || 1,
              size: 'small',
              pageSize: state?.result?.size || 10,
              total: state?.result?.total,
              position: ['bottomRight'],
              showSizeChanger: true,
            }}
          />
        </Col>
      </Row>
    </Card>
  );
};

const ReportSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useParams();
  const [form] = Form.useForm();
  const [channels, updateChannels] = useState([]);
  const { statusList } = useContext(ParcelStatusContext);

  const fetchChannelTypes = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/settings/channels`);
      const list = result || [];
      updateChannels(list);
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const onSearch = useCallback(
    (params: any) => {
      let values = location.search ? queryString.parse(location.search) : {};
      let query = {
        ...values,
        ...params,
      };
      navigate(`/report/${status}?${queryString.stringify(query)}`);
    },
    [location, status]
  );

  useEffect(() => {
    fetchChannelTypes();
  }, []);

  const channelOptions = useMemo(() => {
    return channels.map((item: any) => {
      return {
        label: item.text,
        value: item.code,
      };
    });
  }, [channels]);

  const reasonOptions = useMemo(() => {
    const item = statusList.find((it: any) => it.cd.toUpperCase() === status?.toUpperCase());
    return (item?.shipmentStatusReasons || []).map((item: any) => {
      return {
        label: item.name,
        value: item.cd,
      };
    });
  }, [statusList, status]);

  const initialValues = useMemo(() => {
    const values = location.search ? queryString.parse(location.search) : {};
    const date =
      values?.startDate && values.endDate
        ? [dayjs(values?.startDate as string), dayjs(values.endDate as string)]
        : [null, null];
    return {
      ...values,
      date,
      branchNo: values.branchNo ? Number(values.branchNo) : undefined,
      customerNo: values.customerNo ? Number(values.customerNo) : undefined,
    };
  }, [location.search]);

  if (status == null) {
    return null;
  }

  return (
    <Form form={form} layout="vertical" initialValues={initialValues}>
      <Row gutter={24}>
        <Col span={4}>
          <Form.Item label={dateLabel[status?.toUpperCase()] || 'Скан огноо'} name="date">
            <DatePicker.RangePicker
              onChange={(value) => {
                onSearch({
                  startDate: value?.[0] ? value[0].startOf('day').toISOString() : '',
                  endDate: value?.[1] ? value[1].endOf('day').toISOString() : '',
                });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label={branchLabel[status?.toUpperCase()] || 'Хү.Салбар'} name={'branchNo'}>
            <BranchSelect showSearch allowClear onChange={(e) => onSearch({ branchNo: e })} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item
            label={deliveryManLabel[status?.toUpperCase()] || 'Хү.Хүргэгч'}
            name={'deliveryManNo'}
          >
            <DeliveryManSelect allowClear onChange={(e) => onSearch({ deliveryManNo: e })} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Харилцагч" name={'customerNo'}>
            <CustomerSelect showSearch allowClear onChange={(e) => onSearch({ customerNo: e })} />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Суваг" name={'svc'}>
            <Select
              placeholder="Бүгд"
              options={channelOptions}
              allowClear
              onChange={(e) => onSearch({ svc: e })}
            />
          </Form.Item>
        </Col>
        {status?.toUpperCase() === 'NOT_DELIVERED' && reasonOptions.length > 0 ? (
          <Col span={4}>
            <Form.Item label="Шалтгаан" name={'reason'}>
              <Select
                placeholder="Бүгд"
                options={reasonOptions}
                allowClear
                onChange={(e) => onSearch({ reason: e })}
              />
            </Form.Item>
          </Col>
        ) : null}
      </Row>
    </Form>
  );
};

export default ShippingStatusReport;
